import { Box, Button, Chip, Divider, Stack, Tooltip, Typography, FormControlLabel, Switch } from '@mui/material'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import BuildIcon from '@mui/icons-material/Build'
import PauseIcon from '@mui/icons-material/Pause'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListIcon from '@mui/icons-material/List'
import useMosInfo from '../common/provider/useMosInfo'
import JobStatusChangeDialog from '../FabricationOrderJobReference/PlannedJobs/components/JobStatusChangeDialog/JobStatusChangeDialog'
import PlannedJobCustomModal, { prefetchPlannedJobComponents } from '../FabricationOrderJobReference/PlannedJobs/components/PlannedJobCustomModal/PlannedJobCustomModal'
import PlannedJobListWidgetConfiguration from '../FabricationOrderJobReference/PlannedJobs/PlannedJobListWidgetConfiguration'
import { DEFAULT_VISIBLE_TABS } from '../FabricationOrderJobReference/PlannedJobs/utils/plannedJobTabs'
import useSharedVisibleTabs from '../FabricationOrderJobReference/PlannedJobs/hooks/useSharedVisibleTabs'
import useSharedPlannedByTechnologyIds from '../FabricationOrderJobReference/PlannedJobs/hooks/useSharedPlannedByTechnologyIds'
import WorkerRequiredButton from '../common/WorkerRequiredButton'
import AlertUI from '../../../generic/AlertUI'
import ActiveJobList from '../FabricationOrderJobReference/ActiveMultiJob/components/ActiveJobList'
import BatchBadge from '../common/batch/BatchBadge'
import BatchActionsBar from '../FabricationOrderJobReference/ActiveMultiJob/components/BatchActionsBar'
import BatchBlockedNotice from '../common/batch/BatchBlockedNotice'
import { buildBatchBadges } from '../common/batch/batchLabel'
import { groupByBatchId, useBatchActions } from '../common/batch/useBatchActions'
import { getJobBatchId, getJobExternalCircuit, getJobGovernedByAcceptance, getJobManagePreparation, getJobStatusCode, isJobInPreparation } from '../common/reportOutcome'
import { JOB_WIDGET_ACTION, resolveJobModeActions } from '../common/jobModeActions'
import useJobModeTransition from '../common/useJobModeTransition'
import JobModeChangeDialog from '../common/JobModeChangeDialog'
import PreparationLabel from '../common/PreparationLabel'
import FabricationDocumentsButton from '../common/documents/FabricationDocumentsButton'
import BatchWarningDialog from '../common/batch/BatchWarningDialog'
import BatchFinishDialog from '../common/batch/BatchFinishDialog'
import { resolveBatchWarningKind, BATCH_WARNING_KIND } from '../common/batch/batchWarning'
import { JOB_ACTION_INTENT } from '../FabricationOrderJobReference/PlannedJobs/utils/jobStatus'

const ROOT_SX = { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 1 }
// ⚠ `flexWrap` es lo que salva S15 en el widget más estrecho: el grupo de la derecha baja a una
// segunda línea en vez de comprimir el botón de documentos hasta dejarlo en icono suelto.
const HEADER_STACK_SX = { flex: '0 0 auto', gap: 1, flexWrap: 'wrap', rowGap: 1 }
const ACTION_BUTTON_SX = { height: 52, textTransform: 'none', fontSize: '1.05rem', borderRadius: 2 }
const PRIMARY_BUTTON_SX = { height: 56, textTransform: 'none', fontSize: '1.05rem', borderRadius: 2 }
const SECONDARY_STACK_SX = { mb: 1 }
const ACTIVE_LIST_BOX_SX = { width: { xs: '100%', md: 140 }, minWidth: { md: 140 }, flexShrink: 0, pr: { md: 0.5 }, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }
const ACTIVE_OPERATION_BOX_SX = {
  px: 1.25,
  py: 1,
  borderRadius: 2,
  bgcolor: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  flexGrow: 1
}
const NO_OPERATION_STACK_SX = {
  width: '100%',
  height: '100%',
  px: 1,
  py: 2,
  borderRadius: 2,
  bgcolor: 'rgba(255, 255, 255, 0.03)',
  border: '1px dashed rgba(255, 255, 255, 0.14)'
}
const COMPACT_JOBS_LIST_INNER_SX = { flex: 1, minHeight: 0, overflowY: 'auto', mt: 1 }
const COMPACT_TOGGLE_BUTTON_SX = { flex: '0 0 auto', textTransform: 'none', borderRadius: 2, justifyContent: 'space-between', borderStyle: 'dashed' }
const ACTIVE_OPERATION_TITLE_SX = { lineHeight: 1.1, fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.25rem' }, wordBreak: 'break-word' }

// Historia 120 (Jupiter, paquete P11) -- S141/R-16: this widget (`SelectFabricationOrderAndJobWidgetProvider`,
// code `SelectFabricationOrderAndJobWidget`, "Operation") is a SIXTH, independently selectable Finish
// surface -- alongside the Planned Jobs tabs and All Active Jobs (P6/P6b), the master-detail (P7/P9),
// Active Single Job (P10) and the legacy compact widget (P11). Wired here with the SAME
// resolver/dialogs those already use (`common/batch/batchWarning.js`, `BatchWarningDialog`,
// `BatchFinishDialog`), never a copy of the condition. No new route, no new field: `getJobBatchId`
// reads the SAME per-job flag `common/reportOutcome.js` already exposes to the other five surfaces,
// off the SAME `activeJobs`/`fabricationOrder`/`jobReference` shape this widget already destructures
// from `useMosInfo()`.
const CLOSED_BATCH_WARNING = { open: false, kind: BATCH_WARNING_KIND.NONE, batchId: '' }
const CLOSED_BATCH_FINISH = { open: false, batchId: '' }

const SelectFabricationOrderAndJobWidget = (props) => {
  const { data, setData } = props
  const { t_ } = useSmartTranslation()
  const [plannedModalOpen, setPlannedModalOpen] = useState(false)
  const { node, selectNode, unSelectNode, fabricationOrder, jobReference, activeJobs, activeJobsProgress, refreshActiveJobs, refreshActiveJobsProgress, globalData, setGlobalData, device, worker } = useMosInfo()

  const hiddenColumnsInitRef = useRef(false)

  useEffect(() => {
    if (hiddenColumnsInitRef.current) return
    hiddenColumnsInitRef.current = true
    if (globalData?.plannedJobListHiddenColumns === undefined) {
      setGlobalData((prev) => ({ ...prev, plannedJobListHiddenColumns: Array.isArray(data?.hiddenColumns) ? data.hiddenColumns : [] }))
    }
  }, [globalData, setGlobalData, data])

  useEffect(() => {
    const shared = globalData?.plannedJobListHiddenColumns
    if (!Array.isArray(shared)) return
    const current = Array.isArray(data?.hiddenColumns) ? data.hiddenColumns : []
    if (shared.length === current.length && shared.every((v, i) => v === current[i])) return
    if (setData) setData({ ...(data || {}), hiddenColumns: shared })
  }, [globalData?.plannedJobListHiddenColumns, data, setData])

  const effectiveHiddenColumns = Array.isArray(globalData?.plannedJobListHiddenColumns)
    ? globalData.plannedJobListHiddenColumns
    : data?.hiddenColumns
  // This widget's configuration popover mounts the very same PlannedJobListWidgetConfiguration as
  // the Planned Jobs widget and opens the very same modal, so the visible tabs are SHARED between
  // the two through the MOS context -- like the column visibility above -- instead of each one
  // keeping a private copy that drifted from the other.
  const effectiveVisibleTabs = useSharedVisibleTabs(data, setData)
  // Story 193 (RN6): the "All Jobs" technology selection is shared the same way.
  const plannedByTechnologyIds = useSharedPlannedByTechnologyIds(data, setData)
  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false, statusCode: 'INTERRUPTED' })
  // S124: the two-action mode modal behind "Change status".
  const [modeChangeOpen, setModeChangeOpen] = useState(false)
  // S36: this widget had no alert surface of its own; the mode transition needs one to tell the
  // operator the job is no longer in progress on this machine (an existing key, no new literal).
  const [AlertElement, showAlert] = AlertUI()
  const [nextActionAfterStatusChange, setNextActionAfterStatusChange] = useState(null)
  const [jobsListOpen, setJobsListOpen] = useState(false)
  // S141 (P11): this widget has no early return above its single top-level return, so there is no
  // hook-order hazard here (§7.2 #9) -- these two just join the other useState calls above.
  const [batchWarning, setBatchWarning] = useState(CLOSED_BATCH_WARNING)
  const [batchFinish, setBatchFinish] = useState(CLOSED_BATCH_FINISH)

  const isCompact = props?.w == null ? false : props.w <= 2
  const hasActiveJobs = activeJobs && activeJobs.length > 0
  const singleJobMode = data?.singleJobMode ?? false
  // D18 addendum (CA11/RN24, story 86): this widget is a MOS surface of its own -- besides mounting
  // the shared planned-jobs cell (S49, gated in useJobColumns), it renders its own Pause/Finish for
  // the active job and a "Change operation" button that INTERRUPTS it before opening the modal.
  // None of the three goes through the derivation points of S48/S50/S51, so the gate is derived
  // here too: D18 closed the scope as ALL MOS surfaces and Sun's guard would reject all three
  // anyway (E1 `JobGovernedByAcceptance`). Not part of the S51 example table -- that scenario
  // enumerates the three active-job widgets and this is not one of them.
  // "Show all" is deliberately left enabled: it opens the same modal without interrupting anything,
  // so the operator always keeps a way off the MOD job.
  // S239/S245 (ENMIENDA 08, G3-12 b) -- los distintivos salen de la MISMA agrupacion y de los MISMOS
  // `activeJobs` que los otros dos widgets (F3): mismo lote, mismo color en los tres. Cero peticiones.
  // 🔴 S248 (ENMIENDA 09, G3-13/R-16) -- ESTE WIDGET MONTA LAS PIEZAS QUE YA EXISTEN: el hook de
  // acciones de lote, la barra, el aviso de bloqueo y el diálogo de cierre que ya tenía. NO evalúa una
  // condición propia: un `if (batchId)` copiado en cada widget es el defecto, porque tres copias de la
  // misma regla divergen en la primera corrección — el origen de las tres superficies de S141.
  const { batchGroups: batchActionGroups, selectedBatchGroup, pauseBatchGroup, blockedPause, closeBlockedPause } = useBatchActions({
    activeItems: activeJobs,
    device,
    worker,
    selectedJob: { fabricationOrderId: fabricationOrder?.id, jobReferenceId: jobReference?.id },
    refreshActiveJobs,
    showAlert,
    t_
  })

  const batchBadges = buildBatchBadges(groupByBatchId(activeJobs, t_), t_)
  const shownJobBatchBadge = batchBadges[getJobBatchId(activeJobs, fabricationOrder, jobReference)] ?? null

  const governedByAcceptance = getJobGovernedByAcceptance(activeJobs, fabricationOrder, jobReference)
  // S80 (story 100, RN34 + C9(2) + R25): a JOB that crossed the external circuit gate is a DIFFERENT
  // domain concept, computed independently here too -- gates the SAME three controls above, on top
  // of (not instead of) the acceptance-form gate. "Show all" stays enabled here as well, and for a
  // STRONGER reason than #86/S61: an external trip can last two weeks, so trapping the operator on
  // it would be far worse UX.
  const externalCircuitJob = getJobExternalCircuit(activeJobs, fabricationOrder, jobReference)
  const actionsDisabled = governedByAcceptance || externalCircuitJob
  const governedByAcceptanceTooltip = governedByAcceptance
    ? t_('Acceptance job: actions are governed by the acceptance form')
    : externalCircuitJob
      ? t_('External job: actions are governed by the external jobs widget')
      : ''

  // Story 113 (S120/S133, R-G): the fourth copy of the layout rule lived here. The two buttons below
  // now render what the shared point resolves, with this widget's own gate feeding into it.
  // S131/RN39: "Show all" and "Change operation" are NOT part of the layout -- the first stays
  // enabled in preparation (the operator is never trapped), and the second keeps its own gate.
  // S141: only preparation is labelled, and only when the switch is on (S151).
  const inPreparation = isJobInPreparation(activeJobs, fabricationOrder, jobReference) &&
    getJobManagePreparation(activeJobs, fabricationOrder, jobReference)
  // Resolved BEFORE the layout: `busy` feeds the Go to execution button's disabled state.
  const { goToExecution, backToPreparation, busy: modeTransitionBusy } = useJobModeTransition({
    device,
    fabricationOrder,
    jobReference,
    worker,
    activeJobs,
    refreshActiveJobs,
    showAlert,
    t_
  })
  const modeActions = resolveJobModeActions({
    managePreparation: getJobManagePreparation(activeJobs, fabricationOrder, jobReference),
    statusCode: getJobStatusCode(activeJobs, fabricationOrder, jobReference),
    finishDisabled: actionsDisabled,
    pauseDisabled: actionsDisabled,
    gatedTooltip: governedByAcceptanceTooltip,
    modeTransitionBusy
  })

  useEffect(() => {
    prefetchPlannedJobComponents().catch(() => {})
  }, [])

  const onClick = useCallback(() => {
    if (jobReference) {
      setNextActionAfterStatusChange('openPlanned')
      setChangeStatusDialog({ open: true, statusCode: 'INTERRUPTED' })
      return
    }
    setPlannedModalOpen(true)
  }, [jobReference])

  // S141/R-16 (P11) -- THE derivation point for this widget's Finish button: the SAME resolver
  // P6/P7/P10/P11 already use (`resolveBatchWarningKind`), fed the SAME per-job flag
  // (`getJobBatchId`). A job with no batch (absent/null/blank) resolves NONE and falls straight into
  // today's individual close -- no behaviour change for the vast majority of jobs, which never
  // belonged to a batch.
  const onFinishClick = useCallback(() => {
    const batchId = getJobBatchId(activeJobs, fabricationOrder, jobReference)
    const kind = resolveBatchWarningKind({ batchId }, JOB_ACTION_INTENT.FINISH)
    if (kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setBatchWarning({ open: true, kind, batchId })
      return
    }
    setChangeStatusDialog({ open: true, statusCode: 'FINISHED' })
  }, [activeJobs, fabricationOrder, jobReference])

  // S129/S134/S136-equivalent for this widget: dismissing the warning fires neither choice.
  const handleBatchWarningClose = () => setBatchWarning(CLOSED_BATCH_WARNING)

  // "Finish only this job" -- today's individual close, unchanged, no batch call of any kind.
  const handleBatchWarningChooseIndividual = () => {
    setBatchWarning(CLOSED_BATCH_WARNING)
    setChangeStatusDialog({ open: true, statusCode: 'FINISHED' })
  }

  // "Finish the whole batch" -- N4+N5 via `BatchFinishDialog` (P8), mounted as-is, no new route.
  const handleBatchWarningChooseBatch = () => {
    const { batchId } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)
    setBatchFinish({ open: true, batchId })
  }

  const handleBatchFinishClose = () => setBatchFinish(CLOSED_BATCH_FINISH)

  const handleBatchFinished = () => {
    setBatchFinish(CLOSED_BATCH_FINISH)
    unSelectNode()
    refreshActiveJobs()
  }

  const onPauseClick = useCallback(() => {
    setChangeStatusDialog({ open: true, statusCode: 'INTERRUPTED' })
  }, [])

  const handleModeAction = {
    [JOB_WIDGET_ACTION.PAUSE]: onPauseClick,
    [JOB_WIDGET_ACTION.FINISH]: onFinishClick,
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: goToExecution,
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: () => setModeChangeOpen(true)
  }
  const MODE_ACTION_ICON = {
    [JOB_WIDGET_ACTION.PAUSE]: <PauseIcon />,
    [JOB_WIDGET_ACTION.FINISH]: <CheckCircleIcon />,
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: <PlayArrowIcon />,
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: <SwapHorizIcon />
  }
  const MODE_ACTION_COLOR = {
    [JOB_WIDGET_ACTION.PAUSE]: 'warning',
    [JOB_WIDGET_ACTION.CHANGE_STATUS]: 'warning',
    [JOB_WIDGET_ACTION.FINISH]: 'error',
    // S122: it takes the finish button's position, so it must not read as the disabled one.
    [JOB_WIDGET_ACTION.GO_TO_EXECUTION]: 'primary'
  }
  const renderModeButton = (descriptor) => (
    <Tooltip title={descriptor.tooltip} placement='top' arrow>
      <Box sx={{ width: '100%' }}>
        <WorkerRequiredButton
          fullWidth
          variant='outlined'
          color={MODE_ACTION_COLOR[descriptor.action]}
          startIcon={MODE_ACTION_ICON[descriptor.action]}
          onClick={handleModeAction[descriptor.action]}
          disabled={descriptor.disabled}
          sx={ACTION_BUTTON_SX}
        >
          {t_(descriptor.label)}
        </WorkerRequiredButton>
      </Box>
    </Tooltip>
  )

  const onStatusDialogCancel = useCallback(() => {
    setChangeStatusDialog(prev => ({ ...prev, open: false }))
  }, [])

  const onStatusDialogSuccess = useCallback(() => {
    if (nextActionAfterStatusChange === 'openPlanned') {
      unSelectNode()
      setPlannedModalOpen(true)
      setNextActionAfterStatusChange(null)
      return
    }
    if (changeStatusDialog.statusCode === 'FINISHED') {
      refreshActiveJobsProgress()
    }
    refreshActiveJobs()
  }, [unSelectNode, refreshActiveJobs, refreshActiveJobsProgress, changeStatusDialog.statusCode, nextActionAfterStatusChange])

  const onPlannedModalClose = useCallback(() => setPlannedModalOpen(false), [])

  const onPlannedJobStarted = useCallback((nodeWithWorkflowDetails, selectedFabricationOrder, jobReferenceWithWorkflowDetails) => {
    setPlannedModalOpen(false)
    selectNode(nodeWithWorkflowDetails, selectedFabricationOrder, jobReferenceWithWorkflowDetails)
  }, [selectNode])

  const onShowAllClick = useCallback(() => setPlannedModalOpen(true), [])

  const onToggleJobsList = useCallback(() => setJobsListOpen(o => !o), [])

  const onCompactJobSelect = useCallback((selectedNode, selectedFo, selectedJr) => {
    setJobsListOpen(false)
    selectNode(selectedNode, selectedFo, selectedJr)
  }, [selectNode])

  const renderBottomButtons = () => (
    <Box sx={{ flex: '0 0 auto', pt: isCompact ? 0 : 1 }}>
      {jobReference
        ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={SECONDARY_STACK_SX}>
            {renderModeButton(modeActions.left)}
            {renderModeButton(modeActions.right)}
          </Stack>
          )
        : null}

      <Tooltip title={governedByAcceptanceTooltip} placement='top' arrow>
        <Box>
          <WorkerRequiredButton
            onClick={onClick}
            variant={jobReference ? 'outlined' : 'contained'}
            color='primary'
            fullWidth
            size='large'
            startIcon={<SwapHorizIcon />}
            disabled={actionsDisabled}
            sx={PRIMARY_BUTTON_SX}
          >
            {jobReference ? t_(isCompact ? 'Change op.' : 'Change operation') : t_('Select operation')}
          </WorkerRequiredButton>
        </Box>
      </Tooltip>
    </Box>
  )

  return (
    <Box sx={ROOT_SX}>
      {AlertElement}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={HEADER_STACK_SX}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <BuildIcon color='primary' />
          <Typography variant='h6'>{t_('Operation')}</Typography>
        </Stack>
        {/* Historia 148 (S1/S4/S13) — el PRIMER punto de MOS. Monta EL botón compartido, no una copia:
            la condición de permiso, el área táctil y la ventana viven en `common/documents`, y este
            widget solo aporta el par de identificadores de su selección. `jobReference.id`, nunca
            `node.parentNode.id` (corrección del as-is nº1).
            ⚠ Vive en la CABECERA, no entre las acciones (rediseño del PO, 2026-09-21): consultar
            documentación no es operar la máquina, y ahí abajo competía con Pausa y Finalización y
            gastaba una fila entera en los tamaños pequeños. No toca ni desplaza «Cambiar operación»,
            «Pausar», «Finalizar» ni «Ver todos» (S4). */}
        <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' }}>
          <FabricationDocumentsButton
            fabricationOrderId={fabricationOrder?.id}
            jobReferenceId={jobReference?.id}
            // Estrecho = solo el icono, por el mismo criterio con el que aquí «Change operation»
            // pasa a «Change op.». Es una desviación de S15 pedida por el PO: ver la nota del botón.
            compact={isCompact}
          />
          <Button
            variant='contained'
            onClick={onShowAllClick}
            sx={{ display: singleJobMode ? 'none' : 'inline-flex' }}
          >
            {t_('Show all')}
          </Button>
        </Stack>
      </Stack>
      {/* S124-S126: exactly two actions behind "Change status". */}
      <JobModeChangeDialog
        open={modeChangeOpen}
        onClose={() => setModeChangeOpen(false)}
        onBackToPreparation={() => { setModeChangeOpen(false); backToPreparation() }}
        onPauseWork={() => { setModeChangeOpen(false); onPauseClick() }}
      />

      {/* S141 (P11): the ONE warning, reused verbatim -- this widget only ever produces
          FINISH_BATCH_MEMBER (it has no Start/group-shortcut/resume gesture of its own). */}
      <BatchWarningDialog
        open={batchWarning.open}
        kind={batchWarning.kind}
        onClose={handleBatchWarningClose}
        onChooseIndividual={handleBatchWarningChooseIndividual}
        onChooseBatch={handleBatchWarningChooseBatch}
      />

      {/* S246/S248: la pareja, en el PIE del widget y con peso secundario -- la MISMA barra del
          maestro-detalle, con la etiqueta y el distintivo de su lote al lado. Una sola pareja, la del
          lote del job mostrado, y ninguna si ese job no tiene lote. */}
      <BatchActionsBar
        group={selectedBatchGroup}
        badge={selectedBatchGroup ? buildBatchBadges(batchActionGroups, t_)[selectedBatchGroup.batchId] : null}
        onPauseBatch={pauseBatchGroup}
        onOpenFinishBatch={(batchId) => setBatchFinish({ open: true, batchId })}
        t_={t_}
      />
      {/* S147: el MISMO renderizador del BLOCKED que usa el maestro-detalle, nunca un segundo. */}
      <BatchBlockedNotice open={blockedPause.open} jobs={blockedPause.jobs} onClose={closeBlockedPause} />

      {/* S141 (P11): "finish the whole batch" -- N4+N5 as-is, no new route (P8's dialog, unmodified). */}
      <BatchFinishDialog
        open={batchFinish.open}
        deviceId={device?.id}
        workerId={worker?.id}
        batchId={batchFinish.batchId}
        onClose={handleBatchFinishClose}
        onFinished={handleBatchFinished}
      />
      {changeStatusDialog.open && (
        <JobStatusChangeDialog
          open
          onCancell={onStatusDialogCancel}
          onSuccess={onStatusDialogSuccess}
          node={node}
          jobReference={jobReference}
          fabricationOrder={fabricationOrder}
          statusCode={changeStatusDialog.statusCode}
          autoReport={changeStatusDialog.statusCode === 'FINISHED'}
        />
      )}
      {plannedModalOpen && (
        <PlannedJobCustomModal
          open
          onClose={onPlannedModalClose}
          onJobStarted={onPlannedJobStarted}
          hiddenColumns={effectiveHiddenColumns}
          visibleTabs={effectiveVisibleTabs}
          plannedByTechnologyIds={plannedByTechnologyIds}
        />
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: isCompact ? 'column' : { xs: 'column', md: 'row' }, gap: 1 }}>
        {!isCompact && hasActiveJobs && !singleJobMode && (
          <Box sx={ACTIVE_LIST_BOX_SX}>
            <ActiveJobList
              items={activeJobs}
              selectedFabricationOrder={fabricationOrder}
              selectedJobReference={jobReference}
              progressByKey={activeJobsProgress}
              onSelectNode={selectNode}
              batchBadges={batchBadges}
            />
          </Box>
        )}

        {isCompact && activeJobs?.length > 1 && !singleJobMode && (
          <Box sx={{ flex: jobsListOpen ? '1 1 auto' : '0 0 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Button
              fullWidth
              variant='outlined'
              color='warning'
              startIcon={<ListIcon />}
              endIcon={
                <ExpandMoreIcon
                  sx={{ transform: jobsListOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                />
              }
              onClick={onToggleJobsList}
              sx={COMPACT_TOGGLE_BUTTON_SX}
            >
              {t_('View jobs')} ({activeJobs.length})
            </Button>
            {jobsListOpen && (
              <Box sx={COMPACT_JOBS_LIST_INNER_SX}>
                <ActiveJobList
                  items={activeJobs}
                  selectedFabricationOrder={fabricationOrder}
                  selectedJobReference={jobReference}
                  progressByKey={activeJobsProgress}
                  onSelectNode={onCompactJobSelect}
                  batchBadges={batchBadges}
                />
              </Box>
            )}
          </Box>
        )}

        {!(isCompact && jobsListOpen) && (
          <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pr: 0.5 }}>
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
              {jobReference
                ? (
                  <Stack spacing={1.25} sx={{ width: '100%', height: '100%' }}>
                    <Box sx={ACTIVE_OPERATION_BOX_SX}>
                      <Stack spacing={1}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ gap: 1 }}>
                          <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1 }}>
                            {t_('Active operation')}
                          </Typography>
                          <Chip size='small' color='success' variant='outlined' label={t_('Active')} sx={{ fontWeight: 700 }} />
                        </Stack>
                        <Stack spacing={0.75}>
                          {/* S245: junto al titulo de la operacion activa, para saber de que lote es
                              el job mostrado SIN desplegar la lista. Mismo componente y mismo color
                              que en la lista y que en los otros dos widgets (S244). */}
                          <Stack direction='row' spacing={0.75} alignItems='center' sx={{ minWidth: 0 }}>
                            <BatchBadge badge={shownJobBatchBadge} />
                            <Typography variant='h6' sx={ACTIVE_OPERATION_TITLE_SX}>
                              {node?.name || '-'}
                            </Typography>
                          </Stack>
                          <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                            {/* S141/RN51: the label sits beside the FO name. D-I6: only preparation. */}
                            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                              {t_('Order')}: <Box component='span' sx={{ color: 'text.primary', fontWeight: 700 }}>{fabricationOrder?.fabricationOrder || '-'}</Box>
                            </Typography>
                            <PreparationLabel visible={inPreparation} />
                            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                              {t_('Workflow')}: <Box component='span' sx={{ color: 'text.primary', fontWeight: 700 }}>{node?.workflow?.name || '-'}</Box>
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                  )
                : (
                  <Stack
                    spacing={0.75}
                    alignItems='center'
                    justifyContent='center'
                    sx={NO_OPERATION_STACK_SX}
                  >
                    <Typography variant='h6' sx={{ fontWeight: 700, textAlign: 'center' }}>
                      {t_('No operation selected')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
                      {t_('Select an operation to start working')}
                    </Typography>
                  </Stack>
                  )}
            </Box>
            {!isCompact && renderBottomButtons()}
          </Box>
        )}
      </Box>

      {isCompact && !jobsListOpen && renderBottomButtons()}
    </Box>
  )
}

class SelectFabricationOrderAndJobWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'SelectFabricationOrderAndJobWidget'

  getName = () => 'Operation'

  getIcon = () => BuildIcon

  createWidget = (props) => <SelectFabricationOrderAndJobWidget {...props} />

  size = { defaultW: 2, defaultH: 4, minW: 2, minH: 4 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size), hideHeader: true }
  }

  getDefaultConfiguration = () => ({ hiddenColumns: [], singleJobMode: false, visibleTabs: DEFAULT_VISIBLE_TABS })

  createConfigurationWidget = (data, setData, t_) => {
    const singleJobMode = data?.singleJobMode ?? false

    const handleToggleSingleJobMode = (e) => {
      setData({ ...data, singleJobMode: e.target.checked })
    }

    return (
      <Box sx={{ minWidth: 260 }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant='h6' sx={{ mb: 1 }}>
            {t_('Configuration')}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 2, pt: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='subtitle2' color='text.secondary'>
            {t_('Job Mode Settings')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={singleJobMode}
                onChange={handleToggleSingleJobMode}
                color='primary'
                size='small'
              />
            }
            label={t_('Single Job Mode')}
          />
          <Typography variant='caption' color='text.secondary'>
            {t_('When enabled, only one job can be active at a time. The "Show all" button and jobs list will be hidden.')}
          </Typography>
        </Box>
        <Divider />
        <PlannedJobListWidgetConfiguration data={data} setData={setData} t_={t_} showTitle={false} />
      </Box>
    )
  }
}

export { SelectFabricationOrderAndJobWidget }
export default SelectFabricationOrderAndJobWidgetProvider
